import React, { useCallback, useEffect, useState } from "react";
import { Button, TextareaField } from "@shoptet/ui";
import { useTranslation } from "react-i18next";
import { useRequestNewSuggestionMutation } from "../../graphql/generated/graphql";
import { useHandleApolloError } from "../../hooks/useHandleApolloError";
import { CheckmarkIcon, Icon } from "../Icons";

export type SuggestionInputFormStateT = {
  disabledSubmit: boolean;
  handleSubmit: () => void;
  userInputLength: number;
};

type SuggestionInputFormPropsT = {
  buttonText?: string;
  category?: string | null;
  defaultUserInput?: string;
  disabled?: boolean;
  hideButton?: boolean;
  onFormStateChange?: (state: SuggestionInputFormStateT) => void;
  onSubmit?: (userInput: string) => void | Promise<void>;
  refetchSuggestions?: () => void;
  showExample?: boolean;
};

const MINIMUM_LENGTH = 350;
const MAXIMUM_LENGTH = 1000;

export const SuggestionInputForm = ({
  buttonText,
  category,
  defaultUserInput,
  disabled,
  hideButton,
  onFormStateChange,
  onSubmit,
  refetchSuggestions,
  showExample = true,
}: SuggestionInputFormPropsT) => {
  const { t } = useTranslation("translation", { keyPrefix: "Wizard Suggestion Generator Page" });
  const [userInput, setUserInput] = useState(defaultUserInput || "");
  const [sendingRequests, setSendingRequests] = useState(false);
  const handleApolloError = useHandleApolloError();

  const [requestSuggestion] = useRequestNewSuggestionMutation({
    onCompleted() {
      if (typeof refetchSuggestions === "function") {
        refetchSuggestions();
      }
      setSendingRequests(false);
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      setSendingRequests(true);
      const sanitizedInput = userInput.replace(/\s+/g, " ");

      // If onSubmit is provided, call it (wizard flow with image requests)
      if (onSubmit) {
        await onSubmit(sanitizedInput);
      } else {
        // Otherwise just save the suggestion (settings/edit flow)
        await requestSuggestion({ variables: { userInput: sanitizedInput } });
      }
    } catch (error) {
      handleApolloError(error);
      setSendingRequests(false);
    }
  }, [handleApolloError, onSubmit, requestSuggestion, userInput]);

  const userInputLength = userInput.trim().replace(/\s+/g, " ").length;
  const disabledActions = disabled || sendingRequests;
  const invalidLength = userInputLength < MINIMUM_LENGTH || userInputLength > MAXIMUM_LENGTH;
  const disabledSubmit = disabledActions || invalidLength;

  // Expose form state to parent
  useEffect(() => {
    if (onFormStateChange) {
      onFormStateChange({ userInputLength, disabledSubmit, handleSubmit });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInputLength, disabledSubmit]);

  return (
    <>
      <div className="v2form v2form--alignStart">
        <TextareaField
          disabled={disabledActions}
          id="suggestions_text"
          label={t("textarea label")}
          name="suggestions_text"
          rows={10}
          tooltip={t("textarea tooltip")}
          value={userInput}
          onChange={setUserInput}
        />
        <div style={{ marginLeft: "calc(min(340px, 30vw) + 4px)" }}>
          <div className="SuggestionStatus">
            <div
              className="SuggestionStatusMessage"
              style={{ color: invalidLength ? "var(--app-critical)" : "var(--app-brand-green)" }}
            >
              {/* `.shoptet-icon--legacy` gives our own icons an explicit colour, so the glyph does
                  not pick up the critical colour this row is painted in - it has to be asked for. */}
              {invalidLength ? (
                <Icon className="icon-size-16 shoptet-icon-color-error" kind="warning" />
              ) : (
                <CheckmarkIcon />
              )}
              <span>{t(invalidLength ? "character counter message error" : "character counter message success")}</span>
            </div>
            <div className="SuggestionStatusCounter">{t("character counter", { userInputLength })}</div>
          </div>

          {showExample && (
            <p className="SuggestionExampletText" style={{ color: "var(--app-text-muted)" }}>
              <strong>{t("example")}</strong> {t(`example text by category.${category || "UNKNOWN"}`)}
            </p>
          )}
        </div>
      </div>
      {!hideButton && (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Button data-test-id="continue-to-next-step" disabled={disabledSubmit} onClick={handleSubmit}>
            {buttonText}
          </Button>
        </div>
      )}
    </>
  );
};
